/**
 * LibraryAgents — shared agent library.
 *
 * Unlike the per-specialty agent templates nested under each specialty,
 * this page shows a cross-specialty library where each agent can be
 * tagged with one or more specialties. Mirrors the LibraryProtocols
 * UX: specialty filter chips, grid of cards, and a "+ New Agent"
 * button (draft only).
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import * as api from "../api.js";
import { PageHeader, Loading, ErrorBanner } from "../components.jsx";
import { useLibraryEnv, useEnvPrefix } from "../LibraryEnvContext.jsx";

function humanize(slug) {
  return (slug || "")
    .split(/[_-]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

const TYPE_LABEL = {
  orchestrator: "Orchestrator",
  sub_agent: "Sub-agent",
  "sub-agent": "Sub-agent",
};

export default function LibraryAgents() {
  const nav = useNavigate();
  const { env, isLive } = useLibraryEnv();
  const prefix = useEnvPrefix();

  const [agents, setAgents] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [specialty, setSpecialty] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [agentsRes, catRes] = await Promise.all([
          api.listAgents({ mode: env }),
          api.listSpecialtyCatalog().catch(() => ({ data: [] })),
        ]);
        setAgents(
          // Orchestrators are owned by groups, not by the agents
          // library. Filter them out so the library only lists
          // sub-agents. Defensive: skip docs missing both name/key,
          // and treat missing fields as empty strings so sort can't
          // throw on `.localeCompare` of undefined.
          (agentsRes.data || [])
            .filter((a) => a && a.agent_type !== "orchestrator" && (a.name || a.key))
            .sort((a, b) =>
              String(a.name || a.key || "").localeCompare(
                String(b.name || b.key || "")
              )
            )
        );
        setCatalog(catRes.data || []);
        setError(null);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [env]);

  // Available specialty filters: seeded catalog + any custom ones used
  // by the agents themselves.
  const specialtyFilters = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const s of catalog) {
      seen.add(s._id);
      list.push({ id: s._id, label: s.name, color: s.color });
    }
    for (const a of agents) {
      for (const t of a.specialties || []) {
        if (!seen.has(t)) {
          seen.add(t);
          list.push({ id: t, label: humanize(t) });
        }
      }
    }
    list.sort((a, b) => a.label.localeCompare(b.label));
    return [{ id: "all", label: "All specialties" }, ...list];
  }, [catalog, agents]);

  // Apply filters in memory so the UI is instant.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return agents.filter((a) => {
      if (specialty !== "all" && !(a.specialties || []).includes(specialty))
        return false;
      if (q) {
        const hay = `${a.name || ""} ${a.key} ${a.description || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [agents, specialty, search]);

  return (
    <>
      <PageHeader
        title="Agents"
        subtitle="Library of shared agent templates. Each agent can apply to one or more specialties. Tag an existing agent or create a new one."
        breadcrumbs={[
          { label: "Library", to: `${prefix}/platform` },
          { label: "Agents" },
        ]}
        actions={
          isLive ? null : (
            <button
              className="btn primary"
              onClick={() => nav("/library/draft/agents/new")}
            >
              + New Agent
            </button>
          )
        }
      />

      <div className="page-body">
        <ErrorBanner error={error} />

        <div
          className="mb-16"
          style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}
        >
          <div className="btn-row" style={{ flexWrap: "wrap" }}>
            {specialtyFilters.map((s) => (
              <button
                key={s.id}
                className={`btn sm ${specialty === s.id ? "primary" : ""}`}
                onClick={() => setSpecialty(s.id)}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="spacer" />
          <input
            type="text"
            placeholder="Search agents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: "6px 12px",
              fontSize: 13,
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-sm)",
              minWidth: 200,
            }}
          />
          <span className="muted small">
            {filtered.length} agent{filtered.length === 1 ? "" : "s"}
          </span>
        </div>

        {loading ? (
          <Loading />
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <h3>No agents</h3>
            <p>
              {agents.length === 0
                ? isLive
                  ? "The agent library is empty in live. Add an agent in Draft and publish."
                  : "The agent library is empty. Click + New Agent to add one."
                : "No agents match the current filter."}
            </p>
          </div>
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <table className="table agents-table" style={{ border: "none" }}>
              <thead>
                <tr>
                  <th style={{ width: "24%" }}>Name</th>
                  <th>Description</th>
                  <th style={{ width: 160 }}>Groups</th>
                  <th style={{ width: 110 }}>Type</th>
                  <th className="right" style={{ width: 70 }}>Tools</th>
                  <th className="right" style={{ width: 90 }}>Rules</th>
                  <th className="right" style={{ width: 80 }}>Triggers</th>
                  <th className="right" style={{ width: 70 }}>Version</th>
                  <th className="right" style={{ width: 80 }}>Chars</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => {
                  const specialties = Array.isArray(a.specialties) ? a.specialties : [];
                  const toolCount = (Array.isArray(a.tools) ? a.tools : []).length;
                  const protocolCount = (Array.isArray(a.linked_protocols) ? a.linked_protocols : []).length;
                  const triggerCount = (Array.isArray(a.triggers) ? a.triggers : []).length;
                  // `content` is usually a string, but a few legacy agent-template
                  // docs store it as an object. Normalize to string length so the
                  // Chars column doesn't crash on `.toLocaleString()`.
                  const contentStr = typeof a.content === "string"
                    ? a.content
                    : (a.content != null ? JSON.stringify(a.content) : "");
                  const chars = contentStr.length;
                  const typeLabel =
                    TYPE_LABEL[a.agent_type] || a.agent_type || "—";
                  return (
                    <tr
                      key={a.key}
                      className="clickable-row"
                      onClick={() =>
                        nav(`${prefix}/agents/${encodeURIComponent(a.key)}`)
                      }
                    >
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span
                            className="agent-icon-tile"
                            style={{
                              width: 26,
                              height: 26,
                              fontSize: 12,
                              background: a.color || "#455A64",
                              borderRadius: 6,
                            }}
                          >
                            {(a.icon && a.icon.trim()) ||
                              String(a.name || a.key || "?").charAt(0).toUpperCase()}
                          </span>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600 }}>
                              {a.name || a.key}
                            </div>
                            <div className="muted small mono">{a.key}</div>
                          </div>
                        </div>
                      </td>
                      <td className="small">
                        {a.description || (
                          <span className="subtle">no description</span>
                        )}
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 4,
                          }}
                        >
                          {specialties.length === 0 ? (
                            <span className="subtle small">—</span>
                          ) : (
                            specialties.map((s) => (
                              <span key={s} className="badge badge-accent">
                                {humanize(s)}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="muted small">{typeLabel}</td>
                      <td className="right">
                        {toolCount > 0 ? (
                          <span className="mono small">{toolCount}</span>
                        ) : (
                          <span className="subtle">—</span>
                        )}
                      </td>
                      <td className="right">
                        {protocolCount > 0 ? (
                          <span className="mono small">{protocolCount}</span>
                        ) : (
                          <span className="subtle">—</span>
                        )}
                      </td>
                      <td className="right">
                        {triggerCount > 0 ? (
                          <span className="mono small">{triggerCount}</span>
                        ) : (
                          <span className="subtle">—</span>
                        )}
                      </td>
                      <td className="right">
                        <span className="mono small">v{a.version}</span>
                      </td>
                      <td className="right muted small">
                        {chars.toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
